<script>
import lazyLoadComponent from '@router/lazyload-component'
import BaseButtonDialog from '@components/BaseButtonDialog'

import {
  genderFilter,
  orderStatusMap,
  orderStatusFilter,
  bookingStatusFilter,
  orderPaymentMethodFilter,
  orderSourceFilter,
  paymentStatusMapFilter,
} from '@utils/filter-value-to-text'
import formatDate, { formatRequestData } from '@utils/format-date'

import pageMixin from '@src/mixins/pagination'

export default {
  page: {
    title: '訂單查詢',
    meta: [{ name: 'description', content: 'Order List' }],
  },
  components: {
    BaseButtonDialog,
    BaseStateLabel: () => lazyLoadComponent(import('@components/BaseStateLabel')),
    DepositOrderDetail: () => lazyLoadComponent(import('@components/Orders/DepositOrderDetail')),
  },
  filters: {
    genderFilter,
    orderStatusFilter,
    bookingStatusFilter,
    orderPaymentMethodFilter,
    orderSourceFilter,
    formatDate,
    paymentStatusMapFilter,
  },
  mixins: [pageMixin],
  data() {
    return {
      orderStatusMap,
      componentKey: 0,
      createOrderVisible:false,
      editOrderVisible:false,
      drawerOrderData: '',
      getRowKeys(row) {
        return row.id
      },
      filterData: {
        status: '',
        memberId: this.$route.params.memberId,
        paymentMethod:'All',
        dateType: 'create', // create 訂單成立日期、 use 訂單使用日期
        startDate: '',
        endDate: '',
      },
      loadedOrders: [],
      queryProductName: '',
      tryingToGetOrder: false,
      isFilterChange: false,
      appStyle: process.env.VUE_APP_BUILD_MODE === 'admin-app-build'
    }
  },
  computed: {
    fetchDataParams() {
      const routeQuery = this.$route.query
      const queryData = Object.assign(routeQuery, this.pageQuery)
      const { startDate, endDate, dateType, ...filterDataWithoutDate } = this.filterData
      const searchWithDate = startDate && endDate
      const searchParams = searchWithDate
        ? Object.assign(this.filterData, {
            startDate: formatRequestData(startDate),
            endDate: formatRequestData(endDate),
          })
        : filterDataWithoutDate
      return Object.assign(searchParams, queryData)
    },
  },
  watch: {
    currentPage: {
      handler: 'tryToFetchList',
      immediate: true,
    },
    filterData: {
      handler() {
        this.isFilterChange = true
      },
      deep: true,
    },
  },
  methods: {
    tableRowClassName({ row }) {
      const rowStatus = row.status
      const usedStatus = row.groupSize / row.usedSize
      if (rowStatus === 0) return 'order--canceled'
      if (usedStatus === 1) return 'order--used'
    },
    async tryToGetOrder(orderId) {
      try {
        this.tryingToGetOrder = true
        this.drawerOrderData = await this.$store.dispatch('deposit/fetchSingleOrder', orderId)
        this.editOrderVisible = true
      } catch {
      } finally {
        this.tryingToGetOrder = false
      }
    },
    async tryToFetchListFilter() {
      // 當篩選前不在第一頁的時候，跳回第一頁
      if (this.currentPage !== 1) {
        this.currentPage = 1
        return
      }
      const { items, productName, total } = await this.$store.dispatch('deposit/fetchOrderList', this.fetchDataParams)
      this.loadedOrders = items
      this.queryProductName = productName
      this.$nextTick(() => {
        this.$refs.pagination && this.$refs.pagination.updateTotal(total)
      })
    },
    async tryToFetchList() {
      const { items, total } = await this.$store.dispatch('deposit/fetchOrderList',this.fetchDataParams)
      this.loadedOrders = items
      this.$nextTick(() => {
        this.$refs.pagination && this.$refs.pagination.updateTotal(total)
      })
    },
    onCellClick(row, column, cell) {
      if (column.type === 'expand') return
      this.tryToGetOrder(row.id)
    },
    onEditOrderDone(){
      this.tryToFetchList()
    }
  },
}
</script>

<template>
  <div>
    <BasePageHeader
      title="儲值訂單" 
    >
    </BasePageHeader>
    <div class="bg-white">
      <div class="el-table-wrapper">
        <el-table
          v-loading.fullscreen.lock="tryingToGetOrder"
          element-loading-text="取得訂單資訊中"
          :data="loadedOrders"
          :default-sort="{ prop: 'created', order: 'descending' }"
          :row-key="getRowKeys"
          :row-class-name="tableRowClassName"
          class="isBgDark"
          @cell-click="onCellClick"
        >
          <el-table-column v-slot="{ row }" prop="created_at" label="訂單編號" min-width="150" >
            <div class="Jost">{{ `#${row.id}` }}</div>
            <div class="text-secondary-light fs-sm Jost">{{ row.created | formatDate('dateTime') }}</div>
          </el-table-column>
          <el-table-column v-slot="{ row }" label="狀態" prop="paymentMethod" min-width="100" >
            <template v-if="row.status === 0">
              <BaseStateLabel class="text-secondary-light" text>{{ row.status | bookingStatusFilter('text') }}</BaseStateLabel>
              <br />
              <BaseStateLabel class="text-secondary-light fs-sm" text>
                <span v-if="row.paymentMethod !== null">{{ row.paymentMethod | orderPaymentMethodFilter }}</span>
                <span v-else>不指定</span>
              </BaseStateLabel>
            </template>
            <template v-else>
              <BaseStateLabel :state="row.paymentStatus | paymentStatusMapFilter('color')" text>{{ row.paymentStatus | paymentStatusMapFilter('text') }}</BaseStateLabel>
              <br />
              <BaseStateLabel class="text-secondary-light fs-sm" text>
                <span v-if="row.paymentMethod !== null">{{ row.paymentMethod | orderPaymentMethodFilter }}</span>
                <span v-else>不指定</span>
              </BaseStateLabel>
            </template>
          </el-table-column>
          <el-table-column v-slot="{ row }" min-width="140" label="儲值金方案">
            <div class="Jost">
            <b> {{ row.params[0].title }}</b><br>
            <span class="text-secondary-light fs-sm Jost">{{row.point}}點 / 售NT${{ row.allActualPayTotal }}</span>
            </div>
          </el-table-column>
          <el-table-column min-width="40">
            <el-tooltip content="查看訂單詳情" placement="top">
              <el-button type="text">
                <BaseIcon name="ellipsis-v" />
              </el-button>
            </el-tooltip>
          </el-table-column>
        </el-table>
      </div>
      <BasePagination ref="pagination" :current-page.sync="currentPage" />
    </div>
    <BaseButtonDialog only-dialog floating />

    <!-- 編輯訂單 -->
    <el-drawer 
      ref="orderFormEdit" 
      class="only-close"
      :class="{ 'appStyle': appStyle }" 
      :visible.sync="editOrderVisible" 
      :append-to-body = "true"
      :size="$root.isSmallDevice ? '100%' : '90%'" 
      destroy-on-close
      :modal-append-to-body = "false"
      :wrapper-closable = "false"
      custom-class="order-drawer"
      @closed="componentKey++"
    >
      <DepositOrderDetail 
        :key="componentKey" 
        :default-value.sync="drawerOrderData"
        @update="onEditOrderDone"
      />
    </el-drawer>
  </div>
</template>
<style lang="scss" module>
:global {
  :local(.filter) {
    & > div {
      // flex-wrap: wrap;
      .el-form-item {
        width: 20%;

        @include max-sm {
          width: 100% !important;
        }
      }
      & + button {
        align-self: baseline;
      }
    }
  }
}
</style>
